// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { typographyGetNoResultsProps } from "@store/Themes/common/props/typography";
import { useFastenersTableApiParameter } from "@store/Framework/Store/Store/Components/FastenersTable/FastenersTableHooks";
import resetSearchFields from "@store/Framework/Store/Store/Components/FastenersTable/Handlers/ResetSearchFields";
import { useFastenersTableDataView } from "@store/Framework/Store/Store/Data/Fasteners/FastenersHooks";
import loadFasteners from "@store/Framework/Store/Store/Data/Fasteners/Handlers/LoadFasteners";
import AccordionFoldingContainer, {
    accordionFoldingContainerFieldDefinitions,
    AccordionFoldingContainerFieldsProps,
    getAccordionFoldingContainerTab,
} from "@store/Library/Components/AccordionFoldingContainer";
import ProductFastenersCard from "@store/Library/Components/ProductFasteners/ProductFastenersCard";
import ProductFastenersDataTable from "@store/Library/Components/ProductFasteners/ProductFastenersDataTable";
import ProductFastenersFilter from "@store/Library/Components/ProductFasteners/ProductFastenersFilter";
import ProductFastenersPagination from "@store/Library/Components/ProductFasteners/ProductFastenersPagination";
import useIsFirstRender from "@toolkit/Framework/Common/Hooks/useIsFirstRender";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import React, { useEffect } from "react";
import { css } from "styled-components";

const enum fields {
    showLength = "showLength",
    showHeadDiameter = "showHeadDiameter",
    showThread = "showThread",
    showPoint = "showPoint",
    showHead = "showHead",
    showFinish = "showFinish",
}

interface ProductDetailsFastenersProps extends WidgetProps {
    fields: {
        [fields.showLength]: boolean;
        [fields.showHeadDiameter]: boolean;
        [fields.showThread]: boolean;
        [fields.showPoint]: boolean;
        [fields.showHead]: boolean;
        [fields.showFinish]: boolean;
    } & AccordionFoldingContainerFieldsProps;
}

type Props = ProductDetailsFastenersProps & HasProduct;

export interface ProductDetailsFastenersStyles {
    loadingOverlay?: LoadingOverlayProps;
    cardContainer?: InjectableCss;
    noResult?: TypographyPresentationProps;
}

export const productDetailsFastenersStyles: ProductDetailsFastenersStyles = {
    cardContainer: {
        css: css`
            border: 1px solid ${getColor("common.border")};

            > div:not(:last-child) {
                border-bottom: 1px solid ${getColor("common.border")};
            }
        `,
    },
    noResult: typographyGetNoResultsProps(),
};

const styles = productDetailsFastenersStyles;

const ProductDetailsFasteners: React.FC<Props> = ({
    product,
    fields: { showLength, showHeadDiameter, showThread, showPoint, showHead, showFinish, ...otherProps },
}) => {
    const dispatch = useAppDispatch();
    const isFirstRender = useIsFirstRender();
    const fastenersTableApiParameter = useFastenersTableApiParameter();
    const baseFasternersTableDataView = useFastenersTableDataView(product.id);
    const fastenersTableDataView = usePersistentDataView(baseFasternersTableDataView);
    const { value: products } = fastenersTableDataView;
    const hasActiveFilters =
        !!fastenersTableApiParameter.length ||
        !!fastenersTableApiParameter.boxCount ||
        !!fastenersTableApiParameter.headDiameter ||
        !!fastenersTableApiParameter.thread ||
        !!fastenersTableApiParameter.driveType ||
        !!fastenersTableApiParameter.point ||
        !!fastenersTableApiParameter.head ||
        !!fastenersTableApiParameter.finish;
    const tableId = `${product?.id}-fasteners-table`;

    useEffect(() => {
        if (isFirstRender) {
            dispatch(resetSearchFields());
        }
    }, [dispatch, isFirstRender]);

    useEffect(() => {
        if (isFirstRender || !product.id) {
            return;
        }

        dispatch(
            loadFasteners({
                ...fastenersTableApiParameter,
                tableId,
                productId: product.id,
                page: fastenersTableApiParameter.page ?? 1,
            }),
        );
    }, [dispatch, isFirstRender, tableId, product.id, fastenersTableApiParameter]);

    return (
        <AccordionFoldingContainer
            {...otherProps}
            shouldShowOnlyIf={hasActiveFilters || (!!products && products.length > 0)}
        >
            <DataViewLoadingOverlay {...styles.loadingOverlay} dataView={fastenersTableDataView}>
                <ProductFastenersFilter productId={product.id} />
                {!products || products?.length === 0 ? (
                    <Typography {...styles.noResult}>{siteMessage("ProductDetailsFasteners_NoResults")}</Typography>
                ) : (
                    <>
                        <Hidden below="md">
                            <ProductFastenersDataTable
                                product={product}
                                products={products}
                                tableId={tableId}
                                showLength={showLength}
                                showHeadDiameter={showHeadDiameter}
                                showThread={showThread}
                                showPoint={showPoint}
                                showHead={showHead}
                                showFinish={showFinish}
                            />
                        </Hidden>
                        <Hidden above="sm">
                            <StyledWrapper {...styles.cardContainer}>
                                {products?.map(product => (
                                    <ProductFastenersCard
                                        key={product.id}
                                        product={product}
                                        tableId={tableId}
                                        showLength={showLength}
                                        showHeadDiameter={showHeadDiameter}
                                        showThread={showThread}
                                        showPoint={showPoint}
                                        showHead={showHead}
                                        showFinish={showFinish}
                                    />
                                ))}
                            </StyledWrapper>
                        </Hidden>
                        {fastenersTableDataView.pagination && (
                            <ProductFastenersPagination
                                pagination={fastenersTableDataView.pagination}
                                productId={product.id}
                            />
                        )}
                    </>
                )}
            </DataViewLoadingOverlay>
        </AccordionFoldingContainer>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsFasteners),
    definition: {
        displayName: "Product Fasteners",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            ...(accordionFoldingContainerFieldDefinitions as FieldDefinition[]).map(fieldDefinitions => ({
                ...fieldDefinitions,
                tab: getAccordionFoldingContainerTab(),
            })),
            {
                name: fields.showLength,
                displayName: "Show Length",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
            },
            {
                name: fields.showHeadDiameter,
                displayName: "Show Diameter",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
            },
            {
                name: fields.showThread,
                displayName: "Show Thread",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
            },
            {
                name: fields.showPoint,
                displayName: "Show Point",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
            },
            {
                name: fields.showHead,
                displayName: "Show Head",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
            },
            {
                name: fields.showFinish,
                displayName: "Show Finish",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
